/**
 * Sistema Global de Cores - HostVSL
 * 
 * Todas as cores da aplicação devem usar estas constantes
 * para garantir consistência visual em todo o projeto.
 * 
 * COR PRINCIPAL: #1D4FFF (Azul Host)
 */

export const colors = {
  // Cor Principal - Azul Host
  primary: {
    DEFAULT: '#1D4FFF',  // Azul Host oficial
    light: '#4B7BFF',    // Variação clara - hover states suaves
    dark: '#1640CC',     // Variação escura - estados pressionados
    rgb: '29, 79, 255',  // Para uso com rgba()
  },

  // Cores Premium (Custom Knowledge)
  premium: {
    hostBlue: '#1D4FFF',    // Azul Host - primária
    darkBlue: '#0A1A3A',    // Azul escuro profundo - backgrounds premium
    neutral: '#F5F6F8',     // Cinza neutro - seções suaves
    border: '#E8E8E8',      // Borda cinza suave - linhas minimalistas
  },

  // Cores de Estado
  success: {
    DEFAULT: '#4CC38A',  // Verde para sucesso (Custom Knowledge)
    light: '#6DD4A3',
    dark: '#3BA873',
  },
  
  warning: {
    DEFAULT: '#F59E0B',  // Laranja para avisos
    light: '#FBBF24',
    dark: '#D97706',
  },
  
  error: {
    DEFAULT: '#E5484D',  // Vermelho para erros (Custom Knowledge)
    light: '#F87171',
    dark: '#DC2626',
  },
  
  info: {
    DEFAULT: '#1D4FFF',  // Azul Host para informações
    light: '#4B7BFF',
    dark: '#1640CC',
  },

  // Cores Neutras
  neutral: {
    50: '#F9FAFB',
    100: '#F3F4F6',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827',
  },

  // Cores de Fundo
  background: {
    primary: '#FFFFFF',   // Fundo principal
    secondary: '#F9FAFB', // Fundo secundário
    muted: '#F5F6F8',     // Fundo sutil (Custom Knowledge)
    switchCard: '#F8FAFC', // Switch cards
  },

  // Cores de Borda
  border: {
    light: '#E8E8E8',     // Borda suave (Custom Knowledge)
    medium: '#D1D5DB',    // Borda média
    dark: '#9CA3AF',      // Borda escura
    primary: '#1D4FFF',   // Borda azul (hover/focus)
  },

  // Cores de Texto
  text: {
    primary: '#111827',   // Texto principal
    secondary: '#6B7280', // Texto secundário
    muted: '#9CA3AF',     // Texto desbotado
    inverse: '#FFFFFF',   // Texto sobre fundos escuros
  },

  // Cores de Plataformas (caso necessário)
  platforms: {
    hotmart: '#FF5733',
    kirvano: '#4A5568',
    cakto: '#4CC38A',
  },
} as const;

/**
 * Utilitário para converter hex para rgba
 * @param hex - Cor em hexadecimal
 * @param alpha - Transparência (0-1)
 */
export function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Classes Tailwind recomendadas para cada uso
 * Todas usando a cor oficial #1D4FFF (Azul Host)
 */
export const colorClasses = {
  // Botões
  button: {
    primary: 'bg-[#1D4FFF] hover:bg-[#1D4FFF]/90 text-white',
    secondary: 'bg-white border-2 border-[#E8E8E8] hover:border-[#1D4FFF] text-[#374151]',
    ghost: 'hover:bg-[#F5F6F8] text-[#374151]',
  },

  // Cards
  card: {
    default: 'bg-white border-2 border-[#E8E8E8] hover:border-[#1D4FFF]',
    elevated: 'bg-white shadow-md hover:shadow-lg',
    switchCard: 'bg-[#F8FAFC] border border-[#E8E8E8] rounded-[14px]',
  },

  // Badges
  badge: {
    primary: 'bg-[#1D4FFF] text-white',
    success: 'bg-[#4CC38A] text-white',
    warning: 'bg-[#F59E0B] text-white',
    error: 'bg-[#E5484D] text-white',
  },

  // Tabs
  tabs: {
    list: 'bg-[#F6F6F7] p-1 rounded-xl h-11',
    trigger: 'data-[state=active]:bg-[#1D4FFF] data-[state=active]:text-white rounded-lg',
  },

  // Ícones
  icon: {
    active: 'text-[#1D4FFF]',
    inactive: 'text-[#94A3B8]',
  },

  // Texto
  text: {
    primary: 'text-[#111827]',
    secondary: 'text-[#6B7280]',
    muted: 'text-[#9CA3AF]',
  },
} as const;

/**
 * Normaliza uma cor para o formato #RRGGBB (para ColorPicker que só aceita hex)
 * Aceita: #RGB, #RRGGBB, RGB, RRGGBB, rgba(r,g,b,a), rgb(r,g,b)
 */
export function normalizeHexColor(color: string | null | undefined, fallback: string = '#000000'): string {
  if (!color || typeof color !== 'string') return fallback;

  const c = color.trim();

  // Se já está no formato correto #RRGGBB
  if (/^#[0-9A-Fa-f]{6}$/.test(c)) {
    return c.toUpperCase();
  }

  // Se está no formato #RGB, expandir para #RRGGBB
  if (/^#[0-9A-Fa-f]{3}$/.test(c)) {
    return `#${c[1]}${c[1]}${c[2]}${c[2]}${c[3]}${c[3]}`.toUpperCase();
  }

  // Se está sem o #, adicionar
  if (/^[0-9A-Fa-f]{6}$/.test(c)) {
    return `#${c}`.toUpperCase();
  }

  if (/^[0-9A-Fa-f]{3}$/.test(c)) {
    return `#${c[0]}${c[0]}${c[1]}${c[1]}${c[2]}${c[2]}`.toUpperCase();
  }

  // Se está no formato rgba(r,g,b,a) ou rgb(r,g,b), converter para hex
  const rgbaMatch = c.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgbaMatch) {
    const r = parseInt(rgbaMatch[1]).toString(16).padStart(2, '0');
    const g = parseInt(rgbaMatch[2]).toString(16).padStart(2, '0');
    const b = parseInt(rgbaMatch[3]).toString(16).padStart(2, '0');
    return `#${r}${g}${b}`.toUpperCase();
  }

  // Retornar fallback se não reconhecer o formato
  return fallback;
}
